import authService from './auth.service';
import { LeadListResponse, Lead, KanbanStats, LeadCheckActiveResponse } from '../types/lead.types';

const API_URL = '/api/v1';

class LeadService {
  async getLeads(params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    status?: string;
    stage_code?: string | string[];
    assigned_to_user_id?: number;
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    lead_id?: number;
  }): Promise<LeadListResponse> {
    const { limit = 20, offset = 0, sort = 'created_at', order = 'desc', status, stage_code, assigned_to_user_id, customer_name, customer_iin, phone, date_from, date_to, lead_id } = params || {};

    const q = new URLSearchParams();
    q.set('limit', String(limit));
    q.set('offset', String(offset));
    q.set('sort', sort);
    q.set('order', order);
    if (status) q.set('status', status);
    if (stage_code) {
      if (Array.isArray(stage_code)) {
        stage_code.forEach(code => q.append('stage_code', code));
      } else {
        q.set('stage_code', stage_code);
      }
    }
    if (assigned_to_user_id) q.set('assigned_to_user_id', String(assigned_to_user_id));
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (lead_id) q.set('lead_id', String(lead_id));

    const res = await authService.fetchWithAuth(`${API_URL}/leads?${q.toString()}`);
    if (!res.ok) {
      throw new Error('Ошибка загрузки списка заявок');
    }
    const data = (await res.json()) as LeadListResponse;
    // Нормализуем поля лимита/смещения, если сервер вернул числа как строки
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      limit: Number((data as any).limit ?? limit),
      offset: Number((data as any).offset ?? offset),
    };
  }

  async getNewLeads(params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    lead_id?: number;
  }): Promise<LeadListResponse> {
    const { limit = 20, offset = 0, sort = 'created_at', order = 'desc', customer_name, customer_iin, phone, date_from, date_to, lead_id } = params || {};

    const q = new URLSearchParams();
    q.set('limit', String(limit));
    q.set('offset', String(offset));
    q.set('sort', sort);
    q.set('order', order);
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (lead_id) q.set('lead_id', String(lead_id));

    const res = await authService.fetchWithAuth(`${API_URL}/leads/new?${q.toString()}`);
    if (!res.ok) {
      throw new Error('Ошибка загрузки новых заявок');
    }
    const data = (await res.json()) as LeadListResponse;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      limit: Number((data as any).limit ?? limit),
      offset: Number((data as any).offset ?? offset),
    };
  }

  async getAssignedLeads(params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    lead_id?: number;
  }): Promise<LeadListResponse> {
    const { limit = 20, offset = 0, sort = 'created_at', order = 'desc', customer_name, customer_iin, phone, date_from, date_to, lead_id } = params || {};

    const q = new URLSearchParams();
    q.set('limit', String(limit));
    q.set('offset', String(offset));
    q.set('sort', sort);
    q.set('order', order);
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (lead_id) q.set('lead_id', String(lead_id));

    const res = await authService.fetchWithAuth(`${API_URL}/leads/assigned?${q.toString()}`);
    if (!res.ok) {
      throw new Error('Ошибка загрузки назначенных заявок');
    }
    const data = (await res.json()) as LeadListResponse;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      limit: Number((data as any).limit ?? limit),
      offset: Number((data as any).offset ?? offset),
    };
  }

  async getMyLeads(params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    stage_code?: string | string[];
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    lead_id?: number;
  }): Promise<LeadListResponse> {
    const { limit = 20, offset = 0, sort = 'created_at', order = 'desc', stage_code, customer_name, customer_iin, phone, date_from, date_to, status, lead_id } = params || {};

    const q = new URLSearchParams();
    q.set('limit', String(limit));
    q.set('offset', String(offset));
    q.set('sort', sort);
    q.set('order', order);
    if (stage_code) {
      if (Array.isArray(stage_code)) {
        stage_code.forEach(code => q.append('stage_code', code));
      } else {
        q.set('stage_code', stage_code);
      }
    }
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (status) q.set('status', status);
    if (lead_id) q.set('lead_id', String(lead_id));

    const res = await authService.fetchWithAuth(`${API_URL}/leads/my?${q.toString()}`);
    if (!res.ok) {
      throw new Error('Ошибка загрузки моих заявок');
    }
    const data = (await res.json()) as LeadListResponse;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      limit: Number((data as any).limit ?? limit),
      offset: Number((data as any).offset ?? offset),
    };
  }

  async getLead(id: number): Promise<Lead> {
    const res = await authService.fetchWithAuth(`${API_URL}/leads/${id}`);
    if (!res.ok) {
      throw new Error('Не удалось загрузить заявку');
    }
    return (await res.json()) as Lead;
  }

  async getKanbanStats(params?: {
    assigned_to_user_id?: number;
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    lead_id?: number;
    stage_code?: string | string[];
  }): Promise<KanbanStats> {
    const { assigned_to_user_id, customer_name, customer_iin, phone, date_from, date_to, status, lead_id, stage_code } = params || {};

    const q = new URLSearchParams();
    if (assigned_to_user_id !== undefined) {
      q.set('assigned_to_user_id', String(assigned_to_user_id));
    }
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (status) q.set('status', status);
    if (lead_id) q.set('lead_id', String(lead_id));
    if (stage_code) {
      if (Array.isArray(stage_code)) {
        stage_code.forEach(code => q.append('stage_code', code));
      } else {
        q.set('stage_code', stage_code);
      }
    }

    const url = q.toString() ? `${API_URL}/leads/kanban/stats?${q.toString()}` : `${API_URL}/leads/kanban/stats`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      throw new Error('Ошибка загрузки статистики канбана');
    }
    return (await res.json()) as KanbanStats;
  }

  async getMyKanbanStats(params?: {
    customer_name?: string;
    customer_iin?: string;
    phone?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    lead_id?: number;
    stage_code?: string | string[];
  }): Promise<KanbanStats> {
    const { customer_name, customer_iin, phone, date_from, date_to, status, lead_id, stage_code } = params || {};

    const q = new URLSearchParams();
    if (customer_name) q.set('customer_name', customer_name);
    if (customer_iin) q.set('customer_iin', customer_iin);
    if (phone) q.set('phone', phone);
    if (date_from) q.set('date_from', date_from);
    if (date_to) q.set('date_to', date_to);
    if (status) q.set('status', status);
    if (lead_id) q.set('lead_id', String(lead_id));
    if (stage_code) {
      if (Array.isArray(stage_code)) {
        stage_code.forEach(code => q.append('stage_code', code));
      } else {
        q.set('stage_code', stage_code);
      }
    }

    const url = q.toString() ? `${API_URL}/leads/kanban/my-stats?${q.toString()}` : `${API_URL}/leads/kanban/my-stats`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      throw new Error('Ошибка загрузки статистики канбана');
    }
    return (await res.json()) as KanbanStats;
  }

  /**
   * Generic-метод для загрузки лидов с произвольными фильтрами (динамические фильтры).
   * Массивы сериализуются через append, range-объекты через JSON.
   */
  async getLeadsDynamic(params: Record<string, unknown>): Promise<LeadListResponse> {
    const q = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        value.forEach((v) => q.append(key, String(v)));
      } else if (typeof value === 'object') {
        // range или другой объект — передаём как JSON
        q.set(key, JSON.stringify(value));
      } else {
        q.set(key, String(value));
      }
    });

    const res = await authService.fetchWithAuth(`${API_URL}/leads?${q.toString()}`);
    if (!res.ok) {
      throw new Error('Ошибка загрузки списка заявок');
    }
    const data = (await res.json()) as LeadListResponse;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      limit: Number((data as any).limit ?? params.limit ?? 20),
      offset: Number((data as any).offset ?? params.offset ?? 0),
    };
  }

  /**
   * Generic-метод для загрузки канбан-статистики с произвольными фильтрами.
   */
  async getKanbanStatsDynamic(params: Record<string, unknown>): Promise<KanbanStats> {
    const q = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        value.forEach((v) => q.append(key, String(v)));
      } else if (typeof value === 'object') {
        q.set(key, JSON.stringify(value));
      } else {
        q.set(key, String(value));
      }
    });

    const url = q.toString() ? `${API_URL}/leads/kanban/stats?${q.toString()}` : `${API_URL}/leads/kanban/stats`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      throw new Error('Ошибка загрузки статистики канбана');
    }
    return (await res.json()) as KanbanStats;
  }

  async distributeLeads(payload: {
    mode: 'page' | 'all';
    user_ids: number[];
    lead_ids?: number[];
    filters?: Record<string, unknown>;
  }): Promise<void> {
    if (!Array.isArray(payload.user_ids) || payload.user_ids.length === 0) {
      throw new Error('Не выбраны пользователи для распределения');
    }
    const body: Record<string, unknown> = {
      mode: payload.mode,
      user_ids: payload.user_ids,
    };
    if (payload.lead_ids && payload.lead_ids.length > 0) {
      body.lead_ids = payload.lead_ids;
    }
    if (payload.filters && Object.keys(payload.filters).length > 0) {
      body.filters = payload.filters;
    }

    const res = await authService.fetchWithAuth(`${API_URL}/leads:distribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось распределить заявки');
    }
  }

  async createLead(payload: {
    name?: string | null;
    phone?: string | null;
    source_id?: number | null;
    stage_code?: string | null;
    phone_model?: string | null;
    memory_capacity?: string | null;
    battery_capacity_max?: string | null;
    phone_condition?: string | null;
    has_box?: boolean | null;
    assigned_to_user_id?: number | null;
  }): Promise<Lead> {
    const res = await authService.fetchWithAuth(`${API_URL}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Ошибка создания заявки');
    }
    return (await res.json()) as Lead;
  }

  async checkActiveLeads(phone: string): Promise<LeadCheckActiveResponse> {
    const params = new URLSearchParams();
    params.set('phone', phone);

    const url = `${API_URL}/leads/check-active?${params.toString()}`;
    const res = await authService.fetchWithAuth(url, {
      method: 'GET',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось проверить активные заявки');
    }

    const data = (await res.json()) as LeadCheckActiveResponse;
    return {
      has_active: Boolean(data?.has_active),
      leads: Array.isArray(data?.leads) ? data.leads : [],
      detail: typeof data?.detail === 'string' ? data.detail : null,
    };
  }

  async getLeadsByCustomer(customerId: number): Promise<Lead[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/leads/by-customer/${customerId}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить заявки клиента');
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }

  async updateLead(leadId: number, payload: Partial<Lead>): Promise<Lead> {
    const res = await authService.fetchWithAuth(`${API_URL}/leads/${leadId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить заявку');
    }
    return (await res.json()) as Lead;
  }
}

export default new LeadService();
